
import React, { useState, useEffect } from "react";
import { Lock, ChevronRight, ChevronDown } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { navigationModules } from "../../config/navigation";

interface SidebarNavigationProps {
  activeModule: string;
  onModuleChange: (moduleId: string) => void;
}

const SidebarNavigation = ({ activeModule, onModuleChange }: SidebarNavigationProps) => {
  const { canAccessModule } = useAuth();
  const [expandedModules, setExpandedModules] = useState<string[]>([]);

  // Auto-expand parent when a sub-module is active
  useEffect(() => {
    const parent = navigationModules.find(m => m.subModules?.some(sm => sm.id === activeModule));
    if (parent && !expandedModules.includes(parent.id)) {
      setExpandedModules(prev => [...prev, parent!.id]);
    }
  }, [activeModule, expandedModules]);

  return (
    <div className="w-64 bg-white border-r min-h-screen">
      <nav className="p-4 space-y-2">
        {navigationModules.map((module) => {
          const Icon = module.icon;
          const hasAccess = !module.permission || canAccessModule(module.id);
          const hasSubModules = Array.isArray(module.subModules) && module.subModules.length > 0;

          return (
            <div key={module.id}>
              <button
                onClick={() => {
                  if (hasSubModules) {
                    setExpandedModules(prev => prev.includes(module.id)
                      ? prev.filter(id => id !== module.id)
                      : [...prev, module.id]
                    );
                  }
                  if (hasAccess) {
                    onModuleChange(module.id);
                  }
                }}
                disabled={!hasAccess}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                  activeModule === module.id 
                    ? 'bg-green-100 text-green-700' 
                    : hasAccess 
                      ? 'text-gray-700 hover:bg-gray-100' 
                      : 'text-gray-400 cursor-not-allowed'
                }`}
              >
                {hasSubModules ? (
                  expandedModules.includes(module.id) ? (
                    <ChevronDown className="h-4 w-4 mr-2" />
                  ) : (
                    <ChevronRight className="h-4 w-4 mr-2" />
                  )
                ) : null}
                <Icon className="h-5 w-5 mr-1" />
                <span>{module.name}</span>
                {!hasAccess && <Lock className="h-3 w-3 ml-auto" />}
              </button>

              {hasSubModules && expandedModules.includes(module.id) && (
                <div className="mt-1 space-y-1">
                  {module.subModules!.map((sub) => {
                    const SubIcon = sub.icon;
                    const canAccessSub = !sub.permission || canAccessModule(sub.id);
                    const isActiveSub = activeModule === sub.id;

                    return (
                      <button
                        key={sub.id}
                        onClick={() => canAccessSub && onModuleChange(sub.id)}
                        disabled={!canAccessSub}
                        className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ml-6 ${
                          isActiveSub
                            ? 'bg-green-100 text-green-700'
                            : canAccessSub
                              ? 'text-gray-700 hover:bg-gray-100'
                              : 'text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <SubIcon className="h-5 w-5" />
                        <span>{sub.name}</span>
                        {!canAccessSub && <Lock className="h-3 w-3 ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
};

export default SidebarNavigation;
